import { Route, UserLocation } from '../types';

// Calculate distance between two points using Haversine formula
export function calculateDistance(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const R = 3959; // Earth's radius in miles
  const dLat = (lat2 - lat1) * (Math.PI / 180);
  const dLon = (lon2 - lon1) * (Math.PI / 180);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * (Math.PI / 180)) *
      Math.cos(lat2 * (Math.PI / 180)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

// Get user's current location
export function getUserLocation(): Promise<UserLocation> {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is not supported by this browser.'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
        });
      },
      (error) => {
        reject(error);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000,
      }
    );
  });
}

// Sort routes by distance from user location
export function sortRoutesByDistance(routes: Route[], userLocation: UserLocation): Route[] {
  return [...routes].sort((a, b) => {
    const distanceA = calculateDistance(
      userLocation.latitude,
      userLocation.longitude,
      a['Area Latitude'],
      a['Area Longitude']
    );
    const distanceB = calculateDistance(
      userLocation.latitude,
      userLocation.longitude,
      b['Area Latitude'],
      b['Area Longitude']
    );
    return distanceA - distanceB;
  });
}

// Format distance for display
export function formatDistance(distance: number): string {
  if (distance < 1) {
    return `${Math.round(distance * 5280)} ft`;
  }
  return `${distance.toFixed(1)} mi`;
}

// Grade difficulty mapping based on standard climbing grade scale
const GRADE_DIFFICULTY_MAP: Record<string, number> = {
  '3rd': 800,
  '4th': 900,
  '5.0': 1000,
  '5.1': 1100,
  '5.2': 1200,
  '5.3': 1300,
  '5.4': 1400,
  '5.5': 1500,
  '5.6': 1600,
  '5.7': 1800,
  '5.8': 2000,
  '5.9': 2300,
  '5.10': 2500,
  '5.10a': 2600,
  '5.10b': 2700,
  '5.10c': 3100,
  '5.10d': 3300,
  '5.11': 4500,
  '5.11a': 4600,
  '5.11b': 4800,
  '5.11c': 5100,
  '5.11d': 5300,
  '5.12': 6500,
  '5.12a': 6600,
  '5.12b': 6700,
  '5.12c': 7100,
  '5.12d': 7300,
  '5.13': 8500,
  '5.13a': 8600,
  '5.13b': 8700,
  '5.13c': 9200,
  '5.13d': 9500,
  '5.14': 10400,
  '5.14a': 10500,
  '5.14b': 10900,
  '5.14c': 11200,
  '5.14d': 11500,
  '5.15a': 11600,
  '5.15b': 11900,
  '5.15c': 12100,
  '5.15d': 12400,
  // Bouldering grades
  'V0': 1000,
  'V1': 1200,
  'V2': 1400,
  'V3': 1600,
  'V4': 1800,
  'V5': 2000,
  'V6': 2300,
  'V7': 2500,
  'V8': 2800,
  'V9': 3200,
  'V10': 3600,
  'V11': 4000,
  'V12': 4500,
  'V13': 5000,
  'V14': 5500,
  'V15': 6000,
  'V16': 6500,
  'V17': 7000,
};

// Parse grade to difficulty number
export function parseGrade(grade: string | number): number {
  let gradeStr = String(grade);
  
  // Handle grades with slashes like "5.10a/b" - take the first part
  if (gradeStr.includes('/')) {
    gradeStr = gradeStr.split('/')[0];
  }
  
  // Check if it's in our mapping
  if (GRADE_DIFFICULTY_MAP[gradeStr]) {
    return GRADE_DIFFICULTY_MAP[gradeStr];
  }
  
  // Handle grades with + modifier (e.g., "5.9+" should be between 5.9 and 5.10a)
  if (gradeStr.includes('+')) {
    const baseGrade = gradeStr.replace('+', '');
    if (GRADE_DIFFICULTY_MAP[baseGrade]) {
      // Add a small increment to make it slightly harder than the base grade
      return GRADE_DIFFICULTY_MAP[baseGrade] + 50;
    }
  }
  
  // Fallback: try to parse numeric grades like "5.8" 
  const match = gradeStr.match(/(\d+)\.(\d+)/);
  if (match) {
    const baseGrade = `5.${match[2]}`;
    if (GRADE_DIFFICULTY_MAP[baseGrade]) {
      return GRADE_DIFFICULTY_MAP[baseGrade];
    }
  }
  
  // Default to 0 if we can't parse it
  return 0;
}

// Compare two grades - returns true if grade1 is harder than grade2
export function isGradeHarder(grade1: string | number, grade2: string | number): boolean {
  return parseGrade(grade1) > parseGrade(grade2);
}

// Check if a grade is within a range
export function isGradeInRange(grade: string | number, minGrade?: string, maxGrade?: string): boolean {
  if (!minGrade && !maxGrade) return true;
  
  const gradeValue = parseGrade(grade);
  
  if (minGrade && !maxGrade) {
    return gradeValue >= parseGrade(minGrade);
  }
  
  if (!minGrade && maxGrade) {
    return gradeValue <= parseGrade(maxGrade);
  }
  
  if (minGrade && maxGrade) {
    return gradeValue >= parseGrade(minGrade) && gradeValue <= parseGrade(maxGrade);
  }
  
  return true;
}

// Generate unique ID
export function generateId(): string {
  return Date.now().toString(36) + Math.random().toString(36).substr(2);
}

// Parse location string to extract crag and region
export function parseLocation(location: string): { crag: string; region: string } | null {
  // Expected format: "Crag > Region > Red River Gorge > Kentucky"
  const parts = location.split(' > ');
  if (parts.length >= 4 && parts[2] === 'Red River Gorge' && parts[3] === 'Kentucky') {
    return {
      crag: parts[0].trim(),
      region: parts[1].trim()
    };
  }
  return null;
}

// Extract all unique regions from routes
export function extractRegions(routes: Route[]): string[] {
  const regions = new Set<string>();
  routes.forEach(route => {
    const parsed = parseLocation(route.Location);
    if (parsed) {
      regions.add(parsed.region);
    }
  });
  return Array.from(regions).sort();
}

// Extract all unique crags from routes, optionally filtered by region
export function extractCrags(routes: Route[], region?: string): string[] {
  const crags = new Set<string>();
  routes.forEach(route => {
    const parsed = parseLocation(route.Location);
    if (parsed && (!region || parsed.region === region)) {
      crags.add(parsed.crag);
    }
  });
  return Array.from(crags).sort();
}

// Re-export location utilities
export { getRouteCardLocation, groupRoutesByLocation } from './locationUtils';

